import Button from '@orn/shared-web/components/Button/Button';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import { PasswordInput } from '@orn/shared-web/components/Input';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import LoginHeader from '../LoginHeader';
import LoginTitle from '../LoginTitle';

import type { ILoginResponse } from '../../../hooks/useAuthApi';

interface IPasswordStepProps {
  email: string;
  successMessage?: string;
  onNext: (result: ILoginResponse) => void;
  onForgotPassword: () => void;
  onWaitlisted: () => void;
  onBack: () => void;
}

interface IPasswordFormValues {
  password: string;
}

const PasswordStep = ({
  email,
  successMessage,
  onNext,
  onForgotPassword,
  onWaitlisted,
  onBack,
}: IPasswordStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<IPasswordFormValues>({
    defaultValues: { password: '' },
    mode: 'onSubmit',
  });

  const passwordValue = form.watch('password');

  const handleSubmit = React.useCallback(
    async (values: IPasswordFormValues) => {
      setIsSubmitting(true);
      const result = await authApi.login({ email, password: values.password });
      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === 'AUTH_NOT_ON_ALPHA_ALLOW_LIST') {
          onWaitlisted();
          return;
        }
        form.setError('password', { message: 'Invalid email or password.' });
        return;
      }

      onNext(result.data);
    },
    [authApi, email, form, onNext, onWaitlisted]
  );

  return (
    <>
      <LoginHeader onBack={onBack} />
      <LoginTitle title="Welcome back" />

      {successMessage && (
        <div className="bg-success-primary text-success-primary mb-2 rounded-lg px-4 py-3 text-sm">
          {successMessage}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <span className="text-primary text-sm font-medium">Email</span>
        <span className="text-secondary text-sm">{email}</span>
      </div>

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          <FormField control={form.control} name="password">
            {({ field, fieldState }) => (
              <PasswordInput
                {...field}
                autoFocus
                label="Password"
                size="md"
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="current-password"
              />
            )}
          </FormField>

          <Button
            type="submit"
            size="xl"
            color="primary"
            className="w-full"
            isDisabled={!passwordValue}
            isLoading={isSubmitting}
          >
            Sign in
          </Button>
        </div>
      </HookForm>

      <div className="flex w-full justify-center pt-2">
        <Button color="link-color" size="sm" onClick={onForgotPassword}>
          Forgot password?
        </Button>
      </div>
    </>
  );
};

PasswordStep.displayName = 'PasswordStep';

export default PasswordStep;
